/**
 * API service. Calls relative /api/... paths (same-origin via CloudFront in deployed
 * environments; Vite proxy in dev) with a Cognito access token. Types come from the shared
 * OpenAPI-generated contract.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import type { components } from '@telemedicine/shared';

type Case = components['schemas']['Case'];
type CaseSummaryPage = components['schemas']['CaseSummaryPage'];
type CreateCaseRequest = components['schemas']['CreateCaseRequest'];
type Specialty = components['schemas']['Specialty'];
type SpecialtyPage = components['schemas']['SpecialtyPage'];
type CreateSpecialtyRequest = components['schemas']['CreateSpecialtyRequest'];
type UpdateSpecialtyRequest = components['schemas']['UpdateSpecialtyRequest'];
type Reviewer = components['schemas']['Reviewer'];
type PlatformUser = components['schemas']['PlatformUser'];
type Role = components['schemas']['Role'];
type Message = components['schemas']['Message'];
type Recommendation = components['schemas']['Recommendation'];
type RecommendationOutcome = components['schemas']['RecommendationOutcome'];
type DecisionLogEntry = components['schemas']['DecisionLogEntry'];
type Document = components['schemas']['Document'];
type DocumentType = components['schemas']['DocumentType'];
type AllowedContentType = components['schemas']['AllowedContentType'];
type UploadUrlResponse = components['schemas']['DocumentUploadUrlResponse'];
type DownloadUrlResponse = components['schemas']['DocumentDownloadUrlResponse'];

async function idToken(): Promise<string> {
  const session = await fetchAuthSession();
  // API Gateway's Cognito authorizer validates the ID token (checks `aud` = client id).
  // Both tokens carry `sub` and `cognito:groups`, which the handler reads.
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = await idToken();
  const res = await fetch(`/api/v1${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      /* ignore */
    }
    // The API error shape is { error: { code, message } }. Unwrap it into a real Error so
    // callers get a clean, human-readable message (and can still read the code if needed).
    const apiErr = (detail as { error?: { code?: string; message?: string } } | undefined)?.error;
    if (apiErr?.message) {
      const err = new Error(apiErr.message) as Error & { code?: string; status?: number };
      err.code = apiErr.code;
      err.status = res.status;
      throw err;
    }
    throw new Error(`API error: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export function listCases(opts?: {
  status?: string;
  cursor?: string;
  limit?: number;
}): Promise<CaseSummaryPage> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.cursor) params.set('cursor', opts.cursor);
  if (opts?.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return apiFetch<CaseSummaryPage>(`/cases${qs ? `?${qs}` : ''}`);
}

export function getCase(caseId: string): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}`);
}

export function createCase(body: CreateCaseRequest): Promise<Case> {
  return apiFetch<Case>('/cases', { method: 'POST', body: JSON.stringify(body) });
}

export function submitCase(
  caseId: string,
  body: { version: number; assignReviewerId?: string | null; specialtyId?: string | null },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/submit`, { method: 'POST', body: JSON.stringify(body) });
}

export function unassignCase(
  caseId: string,
  body: { version: number; reason?: string | null },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/unassign`, { method: 'POST', body: JSON.stringify(body) });
}

export function changeCaseSpecialty(
  caseId: string,
  body: { version: number; specialtyId: string },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/specialty`, { method: 'POST', body: JSON.stringify(body) });
}

export function changeCasePriority(
  caseId: string,
  body: { version: number; priority: components['schemas']['Priority'] },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/priority`, { method: 'POST', body: JSON.stringify(body) });
}

export function assignCase(
  caseId: string,
  body: { version: number; reviewerId: string; specialtyId?: string | null },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/assign`, { method: 'POST', body: JSON.stringify(body) });
}

export function proposeVideoConsultation(
  caseId: string,
  body: { version: number; scheduledAt: string; note?: string | null },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/video-consultation/propose`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function confirmVideoConsultation(caseId: string, body: { version: number }): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/video-consultation/confirm`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function cancelVideoConsultation(caseId: string, body: { version: number }): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/video-consultation/cancel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

type CaseStatus = components['schemas']['CaseStatus'];

export function transitionCase(
  caseId: string,
  body: { version: number; toStatus: CaseStatus },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${caseId}/transition`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listReviewers(): Promise<{ items: Reviewer[] }> {
  return apiFetch<{ items: Reviewer[] }>('/reviewers');
}

// --- Admin: user management ---

export function listUsers(): Promise<{ items: PlatformUser[] }> {
  return apiFetch<{ items: PlatformUser[] }>('/admin/users');
}

export function createUser(body: {
  email: string;
  givenName: string;
  familyName: string;
  role: Role;
  temporaryPassword?: string;
}): Promise<PlatformUser> {
  return apiFetch<PlatformUser>('/admin/users', { method: 'POST', body: JSON.stringify(body) });
}

export function updateUser(
  username: string,
  body: { givenName?: string; familyName?: string; role?: Role; enabled?: boolean },
): Promise<PlatformUser> {
  return apiFetch<PlatformUser>(`/admin/users/${encodeURIComponent(username)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// --- Messages ---

export function listMessages(caseId: string): Promise<{ items: Message[] }> {
  return apiFetch<{ items: Message[] }>(`/cases/${caseId}/messages`);
}

export function sendMessage(
  caseId: string,
  body: { content: string; sourceLang?: string },
): Promise<Message> {
  return apiFetch<Message>(`/cases/${caseId}/messages`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// --- Recommendations & decision log ---

export function listRecommendations(caseId: string): Promise<{ items: Recommendation[] }> {
  return apiFetch<{ items: Recommendation[] }>(`/cases/${caseId}/recommendation`);
}

export function createRecommendation(
  caseId: string,
  body: {
    version: number;
    outcome: RecommendationOutcome;
    recommendation: string;
    followUp?: string | null;
    sourceLang?: string;
  },
): Promise<Recommendation> {
  return apiFetch<Recommendation>(`/cases/${caseId}/recommendation`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function listDecisionLog(caseId: string): Promise<{ items: DecisionLogEntry[] }> {
  return apiFetch<{ items: DecisionLogEntry[] }>(`/cases/${caseId}/decision-log`);
}

// --- Documents ---

export function listCaseDocuments(caseId: string): Promise<{ items: Document[] }> {
  return apiFetch<{ items: Document[] }>(`/cases/${caseId}/documents`);
}

/**
 * Upload a document via the three-step presigned flow. The SPA hides all of this: the caller
 * just passes a file. Step 1: ask the API for a presigned URL. Step 2: PUT the file straight to
 * S3 (never through the API). Step 3: tell the API the upload completed so it persists metadata.
 */
export async function uploadCaseDocument(
  caseId: string,
  file: File,
  documentType: DocumentType,
): Promise<Document> {
  // 1. Request a presigned upload URL (API validates access + file metadata).
  const presigned = await apiFetch<UploadUrlResponse>(`/cases/${caseId}/documents/upload-url`, {
    method: 'POST',
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type as AllowedContentType,
      fileSize: file.size,
      documentType,
    }),
  });

  // 2. Upload the bytes directly to S3 using the presigned URL (not via our API).
  const putRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers: presigned.requiredHeaders ?? { 'Content-Type': file.type },
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload to storage failed: ${putRes.status}`);

  // 3. Confirm the upload so the API persists document metadata (scan status starts PENDING).
  return apiFetch<Document>(`/cases/${caseId}/documents`, {
    method: 'POST',
    body: JSON.stringify({
      documentId: presigned.documentId,
      s3Key: presigned.s3Key,
      fileName: file.name,
      contentType: file.type as AllowedContentType,
      fileSize: file.size,
      documentType,
    }),
  });
}

/** Get a short-lived presigned download URL (server enforces the scan gate — 409 if not CLEAN). */
export function getCaseDocumentDownloadUrl(documentId: string): Promise<DownloadUrlResponse> {
  return apiFetch<DownloadUrlResponse>(`/documents/${documentId}/download-url`);
}

// Specialty taxonomy is reference data. No client-side caching for now — the server controls
// caching via Cache-Control (no-store in dev so changes show immediately; short max-age in prod).
// A client session cache can be reintroduced later if needed.
export function listSpecialties(launchSubset = true): Promise<{ items: Specialty[] }> {
  return apiFetch<{ items: Specialty[] }>(`/specialties?launchSubset=${launchSubset}`);
}

// --- Admin: specialty taxonomy management (GLOBAL_ADMIN) ---

/** List the full taxonomy incl. inactive, paginated via an opaque cursor. */
export function listAdminSpecialties(opts?: {
  cursor?: string;
  limit?: number;
}): Promise<SpecialtyPage> {
  const qs = new URLSearchParams();
  if (opts?.cursor) qs.set('cursor', opts.cursor);
  if (opts?.limit) qs.set('limit', String(opts.limit));
  const suffix = qs.toString();
  return apiFetch<SpecialtyPage>(`/admin/specialties${suffix ? `?${suffix}` : ''}`);
}

export function createSpecialty(body: CreateSpecialtyRequest): Promise<Specialty> {
  return apiFetch<Specialty>('/admin/specialties', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateSpecialty(
  specialtyId: string,
  body: UpdateSpecialtyRequest,
): Promise<Specialty> {
  return apiFetch<Specialty>(`/admin/specialties/${encodeURIComponent(specialtyId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}
